import { ScenarioFormProvider, useScenarioForm } from './contexts';
import styles from './type-curve-scenario-form.module.scss';
import { AreaPanel } from './area-panel';
import { GlobalConfigs } from './global-configs';
import { AreaConfigs } from './area-configs';
import { Formations } from './formations';
import { InventoryManagement } from './inventory-management';
import { TypeWellProfileConfigs } from './type-well-profile-configs';
import { ScalingNormalizationConfigs } from './scaling-normalization-configs';
import { ValidationConfigs } from './validation-configs';
import type { GlobalConfig } from './types';
import { useEffect, useState } from 'react';
import { EditNotes } from './edit-notes';
import { AreaMap } from './area-map';
import { PageHeader } from 'components/page-header';
import { Button, InlineNotification, Loading } from '@carbon/react';
import { MapBoundary, Notebook } from '@carbon/react/icons';
import { useParams } from 'react-router-dom';
import { typeCurveScenarioDataAtom } from './atoms';
import { useAtom, useAtomValue } from 'jotai';
import { EventsPanel, EventsPanelOpenButton } from 'components/events-panel';
import { ConfigFormHeader } from './config-form-header';

interface TypeCurveScenarioFormProps {
  initialValues?: GlobalConfig;
  commentsUrl?: string;
  eventsUrl?: string;
  areaConfigsUrl?: string;
  pageHref?: string;
  scenarioName?: string;
  readOnly?: boolean;
}

export const TypeCurveScenarioForm = ({
  commentsUrl,
  eventsUrl,
  areaConfigsUrl,
  initialValues,
  pageHref = '/type_curve_scenarios/new',
  scenarioName,
  readOnly = false,
}: TypeCurveScenarioFormProps) => {
  const { id: scenarioId } = useParams<{ id: string }>();
  const [showNotes, setShowNotes] = useState(true);
  const [editingNote, setEditingNote] = useState(false);
  const [showMap, setShowMap] = useState(true);
  const scenarioData = useAtomValue(typeCurveScenarioDataAtom);

  useEffect(() => {
    if (!scenarioData.loading) {
      setShowNotes(true);
      setEditingNote(false);
    }
  }, [scenarioData.loading]);

  return (
    <ScenarioFormProvider readOnly={readOnly}>
      {scenarioData.loading && (
        <div className={styles.loadingContainer}>
          <Loading withOverlay={false} />
        </div>
      )}
      <FormHeader
        pageHref={pageHref}
        scenarioName={scenarioName}
        showEventsPanel={!!(eventsUrl && commentsUrl)}
        showMap={showMap}
        onToggleMap={() => setShowMap((prev) => !prev)}
        showNotes={showNotes}
        onToggleNotes={() => {
          if (showNotes) setEditingNote(false);
          setShowNotes((prev) => !prev);
        }}
      />
      <FormError />

      {!!scenarioId && showNotes && (
        <EditNotes editingNote={editingNote} setEditingNote={setEditingNote} />
      )}

      {showMap && <AreaMap />}

      <div className={styles.layoutContainer}>
        <AreaPanel resourceUrl={areaConfigsUrl} />
        <ConfigForms initialValues={initialValues} />
      </div>

      <EventsPanel
        header={scenarioName || ''}
        title="Type Curve Scenario"
        helperText="Type Curve Scenario context allows you to share information about a scenario."
        eventsHref={eventsUrl}
        commentsHref={commentsUrl}
      />
    </ScenarioFormProvider>
  );
};

interface ConfigFormsProps {
  initialValues?: GlobalConfig;
}

const ConfigForms = ({ initialValues }: ConfigFormsProps) => {
  const { setFieldBaseline, isScenarioDetailsSelected } = useScenarioForm();

  useEffect(() => {
    if (initialValues) {
      setFieldBaseline('globalConfig', initialValues);
    }
  }, [initialValues, setFieldBaseline]);

  return (
    <div className={styles.configFormsContainer}>
      <ConfigFormHeader isScenarioDetailsSelected={isScenarioDetailsSelected} />
      {isScenarioDetailsSelected && <GlobalConfigs />}
      {/*
        Render <AreaConfigs /> based solely on isScenarioDetailsSelected.
        Previously this was also gated on `selectedAreaConfig`, but that
        causes <AreaConfigs /> to unmount/remount on save: in React 17 the
        post-save state updates aren't auto-batched, so there is a brief
        intermediate render where areaConfigs has the new server id but
        selectedConfigId is still 'local' — which makes the find return
        null. AreaConfigs handles a null selectedAreaConfig internally
        (every read uses optional chaining and the JSX gates on
        form.values.areaConfig), so we drop the guard.
      */}
      {!isScenarioDetailsSelected && (
        <>
          <AreaConfigs />
          <Formations />
          <InventoryManagement />
          <TypeWellProfileConfigs />
          <ScalingNormalizationConfigs />
          <ValidationConfigs />
        </>
      )}
    </div>
  );
};

interface FormHeaderProps {
  pageHref?: string;
  scenarioName?: string;
  showEventsPanel?: boolean;
  showMap?: boolean;
  onToggleMap?: () => void;
  showNotes?: boolean;
  onToggleNotes?: () => void;
}

const FormHeader = ({
  pageHref = '/type_curve_scenarios/new',
  scenarioName,
  showEventsPanel = false,
  showMap = true,
  onToggleMap,
  showNotes = false,
  onToggleNotes,
}: FormHeaderProps) => {
  const { id: scenarioId } = useParams<{ id: string }>();

  return (
    <PageHeader
      breadcrumbs={[
        ['/type_curves?tab=2', 'Type Curve Scenarios'],
        [pageHref, scenarioName || 'Create New Type Curve Scenario'],
      ]}
      title={
        scenarioName
          ? `Type Curve Scenarios - ${scenarioName}`
          : 'Create New Type Curve Scenario'
      }
      actions={
        <div className={styles.actionButtonsContainer}>
          <Button
            kind={showMap ? 'tertiary' : 'ghost'}
            size="md"
            hasIconOnly
            renderIcon={MapBoundary}
            iconDescription="Toggle Map"
            onClick={onToggleMap}
          />
          {!!scenarioId && (
            <Button
              kind={showNotes ? 'tertiary' : 'ghost'}
              size="md"
              hasIconOnly
              renderIcon={Notebook}
              iconDescription="Toggle Notes"
              onClick={onToggleNotes}
            />
          )}
          {showEventsPanel && <EventsPanelOpenButton />}
        </div>
      }
    />
  );
};

/**
 * The error from the last failed save. It stays until the user closes it, the
 * next save starts, or another config is selected.
 * @return {JSX.Element | null} The error banner, or null when there is none
 */
const FormError = () => {
  const [scenarioDataState, setScenarioDataState] = useAtom(
    typeCurveScenarioDataAtom
  );

  if (!scenarioDataState.error) return null;

  return (
    <div className={styles.inlineNotificationsContainer}>
      <InlineNotification
        className={styles.inlineNotification}
        kind="error"
        title="Error"
        lowContrast
        subtitle={scenarioDataState.error}
        onClose={() => {
          setScenarioDataState((prev) => ({ ...prev, error: null }));
          return true;
        }}
      />
    </div>
  );
};
